import { ContractFunnelDto, GuaranteeDto, SidebarComponentDto } from "api/pricing";
import { ContractFunnel, GuaranteesReferential } from "features/subscription-process/pricing-process/types/interfaces";
import { createContractFunnelHash } from "./createContractFunnelHash";

interface FilterItem {
  id: string;
  label: string;
  value: boolean;
}

interface Filter {
  type: string;
  title: string;
  items: FilterItem[];
}

// Constantes pour les clés de composants sidebar
const SIDEBAR_COMPONENT_KEYS = {
  PRICING_FILTERS: 'PRICING_FILTERS',
} as const;

// Type pour la config des filtres reçue de l'API
interface ApiPricingFilterConfig {
  type: string;
  title: string;
  items: Array<{ id: string; label: string }>;
}

/**
 * Parse les composants sidebar génériques de l'API et extrait les filtres
 * Accepte directement un tableau de SidebarComponentDto (structure plate)
 */
export const createFiltersFromFunnels = (sidebar: SidebarComponentDto[]): Filter[] => {
  const result: Filter[] = [];

  for (const component of sidebar) {
    if (component.componentKey === SIDEBAR_COMPONENT_KEYS.PRICING_FILTERS) {
      // componentConfig est un tableau de PricingFilterConfig
      const filterConfigs = component.componentConfig as ApiPricingFilterConfig[];

      for (const filterConfig of filterConfigs) {
        result.push({
          type: filterConfig.type,
          title: filterConfig.title,
          items: filterConfig.items.map(item => ({
            id: item.id || '',
            label: item.label || '',
            value: true // Initialisé à true pour tout afficher par défaut
          }))
        });
      }
    }
  }

  // Filtrer les catégories vides
  return result.filter(filter => filter.items.length > 0);
};

export const createGuaranteesReferential = (guarantees: GuaranteeDto[]): GuaranteesReferential => {
  return guarantees.reduce<GuaranteesReferential>((acc, current) => {
    if (current.isRequired) {
      acc.mandatory.push(current);
    } else {
      acc.optional.push(current);
    }
    return acc;
  }, { optional: [], mandatory: [] });
};

// export const createContractFunnelsFromFunnels = (funnels: ContractFunnelDto[], guaranteesProductIds?: string[]): ContractFunnel[] => {
//   const contractFunnels = funnels.reduce((acc, current) => {
//     current.supports.forEach((support: any) => {
//       if (support.associatedContracts) {
//         const guaranteesReferential = createGuaranteesReferential(current.guarantees);
//         const mandatoryGuarantees = current.guarantees.filter(g => g.isRequired)
//         const newGuarantees = guaranteesProductIds && current.guarantees.filter((g) => {
//           if (g.isRequired) {
//             return true
//           }
//           return guaranteesProductIds.includes(g.id)
//         })
//
//         const warningMessages = current.variables
//           .filter(variable => variable.warningMessage)
//           .map(variable => variable.warningMessage!)
//
//         support.associatedContracts.forEach((contract: any) => {
//           const contractToUpdate = {
//             id: current.id,
//             name: current.name,
//             warningMessages: warningMessages.length > 0 ? warningMessages : undefined,
//             product: current.product,
//             variables: current.variables,
//             fees: current.fees,
//             guarantees: newGuarantees || mandatoryGuarantees,
//             guaranteesReferential,
//             tarification: contract.tarification,
//             supportId: support.id,
//             supportLabel: support.label,
//             contractIdentifier: contract.contractIdentifier,
//             matchScore: contract.matchScore,
//             missingGuarantees: contract.missingGuarantees,
//             hash: ''
//           };
//
//           contractToUpdate.hash = createContractFunnelHash(contractToUpdate);
//           acc.push(contractToUpdate);
//         });
//       }
//     });
//     return acc;
//   }, [] as ContractFunnel[]);
//
//   return contractFunnels.sort((a: any, b: any) => {
//     const matchScoreA = a.matchScore;
//     const matchScoreB = b.matchScore;
//     if (!matchScoreA || !matchScoreB) {
//       return 0;
//     }
//     return matchScoreB - matchScoreA;
//   }); // Tri par matchScore décroissant
// };

export const createContractFunnelsFromFunnels = (funnels: ContractFunnelDto[], guaranteesProductIds?: string[]): ContractFunnelDto[] => {
  const contractFunnels = funnels.map((funnel) => {

    const guaranteesReferential = createGuaranteesReferential(funnel.guarantees);
    const mandatoryGuarantees = funnel.guarantees.filter(g => g.isRequired)
    const newGuarantees = guaranteesProductIds && funnel.guarantees.filter((g) => {
      if (g.isRequired) {
        return true
      }
      return guaranteesProductIds.includes(g.id)
    })

    const warningMessages = funnel.variables
      .filter(variable => variable.warningMessage)
      .map(variable => variable.warningMessage!)


    const newContract: ContractFunnelDto = {
      id: funnel.id,
      name: funnel.name,
      product: funnel.product,
      variables: funnel.variables,
      fees: funnel.fees,
      pricing: funnel.pricing,
      supportId: funnel.supportId,
      supportCode: funnel.supportCode,
      supportLabel: funnel.supportLabel,
      contractIdentifier: funnel.contractIdentifier,
      contractStatutCode: funnel.contractStatutCode,
      matchScore: funnel.matchScore,
      missingGuarantees: funnel.missingGuarantees,
      warningMessages: warningMessages.length > 0 ? warningMessages : undefined,
      // Éligibilité produit / tarif : on propage tel quel (isSuccess=false => grisé).
      isSuccess: funnel.isSuccess,
      errorMessage: funnel.errorMessage,
      guarantees: newGuarantees || mandatoryGuarantees,
      guaranteesReferential,
      hash: ''
    };

    newContract.hash = createContractFunnelHash(newContract);
    return newContract

  });

  return contractFunnels.sort((a, b) => {
    // Tri primaire : type de cartouche (individuel → groupe → cadre), imposé quel que soit le score.
    const rankDiff = supportDisplayRank(a) - supportDisplayRank(b);
    if (rankDiff !== 0) {
      return rankDiff;
    }
    // Tri secondaire : matchScore décroissant au sein de chaque famille.
    const matchScoreA = a.matchScore;
    const matchScoreB = b.matchScore;
    if (!matchScoreA || !matchScoreB) {
      return 0;
    }
    return matchScoreB - matchScoreA;
  });
};

/**
 * Formula cards: a fixed formula is subscribed as is, so every guarantee of the funnel is kept
 * (no mandatory/selected filtering) and the order is the one of the response (no score).
 * The hash is the same as a comparable funnel's, so the cart handles both alike.
 */
export const createFormulaFunnels = (formulas: ContractFunnelDto[]): ContractFunnelDto[] =>
  formulas.map((formula) => {
    const warningMessages = formula.variables
      .filter(variable => variable.warningMessage)
      .map(variable => variable.warningMessage!)

    const newFormula: ContractFunnelDto = {
      ...formula,
      warningMessages: warningMessages.length > 0 ? warningMessages : undefined,
      guaranteesReferential: createGuaranteesReferential(formula.guarantees),
      hash: ''
    };
    newFormula.hash = createContractFunnelHash(newFormula);
    return newFormula;
  });

// Ordre d'affichage du tarificateur : tunnels individuels d'abord, puis propositions
// de contrat de groupe, puis contrats cadres. Cartouches sans support connu en dernier.
const SUPPORT_DISPLAY_ORDER: Record<string, number> = {
  SOLO: 0,
  GROUP: 1,
  CADRE: 2,
};

const supportDisplayRank = (funnel: ContractFunnelDto): number =>
  SUPPORT_DISPLAY_ORDER[funnel.supportCode ?? ""] ?? 3;
